import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, Sparkles, Loader2 } from "lucide-react";

const TITLES = {
  site: "Configurações gerais", i18n: "Traduções PT/EN/ES", stats: "Números / Stats",
  timeline: "Linha do tempo", pillars: "4 Pilares", reasons: "15 Motivos",
  marquee: "Marquee de serviços", case_categories: "Categorias de cases", insight_topics: "Assuntos de insights",
  departments: "Departamentos", service_options: "Opções de contratação", client_categories: "Categorias de clientes",
  home_sections: "Textos / blocos da Home", sobre: "Textos da Sobre",
  careers_config: "Config Carreiras", contact_extras: "Extras do Contato",
  seo: "SEO global",
};

/**
 * Recursively strip out any HTML-like tags, JSON structure and the
 * `translations` sub-tree so that the AI translator only translates the
 * top-level string fields the CMS actually renders. Returns a flat object
 * {field_path: string_value}.
 */
function extractTranslatableFlat(obj, prefix = "") {
  const out = {};
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return out;
  for (const [k, v] of Object.entries(obj)) {
    if (k === "translations") continue;
    const path = prefix ? `${prefix}.${k}` : k;
    if (typeof v === "string" && v.trim()) {
      out[path] = v;
    }
  }
  return out;
}

export default function KVEditor() {
  const { key } = useParams();
  const [value, setValue] = useState("");
  const [original, setOriginal] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/content/kv/${key}`).then(({ data }) => {
      const json = JSON.stringify(data, null, 2);
      setValue(json); setOriginal(json);
    }).catch(() => { setValue("null"); setOriginal("null"); }).finally(() => setLoading(false));
  }, [key]);

  const save = async () => {
    try {
      const parsed = JSON.parse(value);
      setErr("");
      setSaving(true);
      await api.put(`/content/kv/${key}`, { value: parsed });
      setOriginal(value);
      toast.success("Salvo. A versão anterior foi guardada no histórico.");
    } catch (e) {
      if (e instanceof SyntaxError) { setErr("JSON inválido: " + e.message); return; }
      toast.error("Erro ao salvar.");
    } finally { setSaving(false); }
  };

  const translateAll = async () => {
    let parsed;
    try { parsed = JSON.parse(value); } catch { toast.error("JSON inválido — corrija antes de traduzir."); return; }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      toast.error("Esta KV não é um objeto — só objetos com campos de texto podem ser traduzidos automaticamente.");
      return;
    }
    const flat = extractTranslatableFlat(parsed);
    if (Object.keys(flat).length === 0) { toast.error("Nenhum campo de texto encontrado."); return; }
    setTranslating(true);
    try {
      const { data } = await api.post("/admin/translate", {
        fields: flat, target_langs: ["en", "es"], context: "generic", preserve_html: true,
      });
      parsed.translations = { ...(parsed.translations || {}), en: { ...(parsed.translations?.en || {}), ...(data.en || {}) }, es: { ...(parsed.translations?.es || {}), ...(data.es || {}) } };
      setValue(JSON.stringify(parsed, null, 2));
      toast.success("Traduções EN/ES preenchidas em `translations`. Revise e salve.");
    } catch { toast.error("Falha ao traduzir. Verifique a chave LLM."); }
    finally { setTranslating(false); }
  };

  const reset = () => { setValue(original); setErr(""); };
  const isDirty = value !== original;

  return (
    <div>
      <div className="flex justify-between items-end mb-6 flex-wrap gap-2">
        <div><h1 className="font-display text-3xl">{TITLES[key] || key}</h1><div className="text-xs text-neutral-500 mt-1">Edite em JSON. Cada alteração gera uma nova versão.<br />Adicione um bloco <code>"translations": {`{"en":{...},"es":{...}}`}</code> para valores em outros idiomas — ou use o botão abaixo.</div></div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={translateAll} disabled={translating || loading} data-testid="kv-translate" className="text-xs px-3 py-2 border border-accent-orange text-accent-orange rounded-full hover:bg-accent-orange hover:text-white disabled:opacity-40">
            {translating ? <><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Traduzindo…</> : <><Sparkles className="w-3 h-3 inline mr-1" /> Traduzir EN+ES via IA</>}
          </button>
          <button onClick={reset} disabled={!isDirty} className="text-xs px-3 py-2 border border-black/15 rounded-full hover:bg-black/5 disabled:opacity-40"><RotateCcw className="w-3 h-3 inline mr-1" /> Desfazer</button>
          <button onClick={save} disabled={!isDirty || saving} data-testid="kv-save" className="text-xs px-4 py-2 bg-black text-white rounded-full hover:bg-accent-orange disabled:opacity-40"><Save className="w-3 h-3 inline mr-1" /> {saving ? "Salvando…" : "Salvar"}</button>
        </div>
      </div>
      {err && <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-3 rounded mb-3" data-testid="kv-error">{err}</div>}
      {loading ? <div className="text-sm text-neutral-500">Carregando…</div> : (
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          data-testid="kv-editor"
          spellCheck={false}
          className="w-full font-mono text-xs border border-black/15 rounded p-4 outline-none focus:border-black"
          style={{ minHeight: "60vh" }}
        />
      )}
    </div>
  );
}
